import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, MapPin, Sun, Check, X, MessageCircle, Send, Star, Share2, Video, Users } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppDock from "@/components/WhatsAppDock";
import { photoSrc } from "@/components/Reviews";
import { api, waLink } from "@/lib/api";
import WishlistDock, { HeartButton } from "@/components/WishlistDock";
import { shareLink } from "@/components/Logo";
import TourVideo from "@/components/tour/TourVideo";
import TourGalleryStrip from "@/components/tour/TourGalleryStrip";
import PackageEnquiry from "@/components/tour/PackageEnquiry";

function useSeo(tour) {
  useEffect(() => {
    if (!tour) return;
    const prevTitle = document.title;
    document.title = `${tour.title} · ${tour.duration} · ${tour.price} | Random Rider Puri`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = `${tour.description} Book ${tour.title} with Random Rider, Puri — ${tour.price}, ${tour.duration}. WhatsApp +91 90400 03444.`;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "tour-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "TouristTrip", name: tour.title, description: tour.description,
      image: [tour.image, ...(tour.gallery || [])], touristType: tour.category === "local" ? "Puri sightseeing" : "Odisha tour",
      itinerary: { "@type": "ItemList", itemListElement: (tour.itinerary || []).map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.title, description: s.details })) },
      offers: { "@type": "Offer", price: String(tour.price || "").replace(/[^0-9]/g, ""), priceCurrency: "INR", availability: "https://schema.org/InStock" },
      provider: { "@type": "TravelAgency", name: "Random Rider", telephone: "+91 90400 03444", address: { "@type": "PostalAddress", addressLocality: "Puri", addressRegion: "Odisha", addressCountry: "IN" } },
    });
    document.head.appendChild(ld);
    return () => { document.title = prevTitle; meta.content = prevDesc; ld.remove(); };
  }, [tour]);
}

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [droneAddon, setDroneAddon] = useState(null);
  const [drone, setDrone] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    setTour(null);
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).catch(() => setError(true));
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [slug]);
  useEffect(() => { api.get("/addons").then((r) => setDroneAddon(r.data.find((a) => a.id === "drone") || null)).catch(() => {}); }, []);
  useSeo(tour);

  if (error) return (
    <main className="min-h-screen bg-sand flex flex-col items-center justify-center gap-4" data-testid="tour-not-found">
      <p className="font-serif text-3xl text-ink">Tour not found</p>
      <Link to="/" className="text-terra font-semibold">← Back to Random Rider</Link>
    </main>
  );
  if (!tour) return <div className="min-h-screen bg-sand flex items-center justify-center text-ink/50 text-sm">Loading…</div>;

  const days = [...new Set((tour.itinerary || []).map((s) => s.day))];
  const droneLine = drone && droneAddon ? ` Please also add the ${droneAddon.name} (${droneAddon.videos} aerial clips, ${droneAddon.minutes} min total, ₹${droneAddon.price}).` : "";
  const wa = waLink(`Hi Random Rider! I want to book the "${tour.title}" tour (${tour.price}, ${tour.duration}). Please share availability.${droneLine}`);

  return (
    <main className="bg-white min-h-screen" data-testid="tour-detail-page">
      <Nav />
      <section className="relative mt-[72px] overflow-hidden bg-ink" data-testid="tour-hero">
        <img src={photoSrc(tour.image)} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[60vh] flex flex-col justify-end pt-16 pb-12 text-white ${tour.video_url ? "lg:flex-row lg:items-end lg:justify-between gap-10" : ""}`}>
          <div className="flex-1 min-w-0">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/70 hover:text-white mb-6 w-max" data-testid="tour-back-link"><ArrowLeft size={13} /> All tours</Link>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">{tour.category === "local" ? "Puri Local Sightseeing" : "All-Odisha Package"}</p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl" data-testid="tour-detail-title">{tour.title}</motion.h1>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <span className="flex items-center gap-1.5 bg-sand/15 backdrop-blur px-3.5 py-1.5 rounded-full" data-testid="tour-detail-duration"><Clock size={14} /> {tour.duration}</span>
            {tour.pickup && <span className="flex items-center gap-1.5 bg-sand/15 backdrop-blur px-3.5 py-1.5 rounded-full"><MapPin size={14} /> {tour.pickup}</span>}
            {tour.best_season && <span className="flex items-center gap-1.5 bg-sand/15 backdrop-blur px-3.5 py-1.5 rounded-full"><Sun size={14} /> {tour.best_season}</span>}
            <button onClick={() => shareLink("tours", tour.slug, tour.title, () => toast.success("Share link copied — paste it in WhatsApp"))}
              className="flex items-center gap-1.5 bg-terra px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors" data-testid="tour-share-button"><Share2 size={14} /> Share</button>
            <HeartButton item={{ id: `tour-${tour.id}`, type: "tour", name: tour.title, slug: tour.slug, image: tour.image, meta: `${tour.duration} · ${tour.price}` }} />
          </div>
          </div>
          {tour.video_url && <TourVideo url={tour.video_url} title={tour.title} />}
        </div>
      </section>
      <TourGalleryStrip images={tour.gallery} title={tour.title} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-12">
          <p className="text-lg text-ink/80 leading-relaxed" data-testid="tour-detail-description">{tour.description}</p>

          {tour.highlights?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="tour-detail-highlights">
              {tour.highlights.map((h) => (
                <div key={h} className="flex items-center gap-2.5 bg-white border border-ink/10 rounded-xl px-4 py-3 text-sm font-medium text-ink"><Star size={14} className="text-gold fill-gold shrink-0" /> {h}</div>
              ))}
            </div>
          )}

          {tour.itinerary?.length > 0 && (
            <div data-testid="tour-detail-itinerary">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">Day-wise itinerary</p>
              <h2 className="font-serif text-3xl font-bold text-ink mb-8">How the trip unfolds</h2>
              <div className="space-y-8">
                {days.map((d) => (
                  <div key={d} className="grid grid-cols-12 gap-4" data-testid={`tour-itinerary-day-${d}`}>
                    <div className="col-span-3 sm:col-span-2"><span className="inline-block bg-ink text-sand font-serif font-bold px-4 py-2 rounded-full text-sm">Day {d}</span></div>
                    <ol className="col-span-9 sm:col-span-10 relative border-l-2 border-terra/30 ml-1 space-y-6">
                      {tour.itinerary.filter((s) => s.day === d).map((s, i) => (
                        <li key={i} className="pl-6 relative">
                          <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-terra" />
                          <h3 className="font-serif text-xl font-bold text-ink">{s.title}</h3>
                          <p className="text-sm text-ink/70 leading-relaxed mt-1">{s.details}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-6">
            {tour.pricing?.length > 0 && (
              <div className="sm:col-span-2 bg-gold/20 border border-gold/40 rounded-2xl px-5 py-4 text-sm text-ink" data-testid="tour-detail-promise">
                <span className="font-semibold">You only book your journey to Puri and back home.</span> From the moment you arrive, every arrangement — AC rooms, all meals, private car with driver, sightseeing and drop — is taken care of by Random Rider.
              </div>
            )}
            {tour.pricing?.length > 0 && (
              <div className="bg-sand border border-ink/10 text-ink rounded-2xl p-6 sm:col-span-2" data-testid="tour-detail-pricing">
                <p className="font-mono text-[11px] uppercase tracking-widest text-terra mb-1">Package price · standard room occupancy</p>
                <p className="text-xs text-ink/60 mb-4">Total for the whole group · luxury stay rate may differ · {tour.best_season}</p>
                <div className="grid sm:grid-cols-2 gap-x-8">
                  {tour.pricing.map((r) => (
                    <div key={r.label} className="flex items-center justify-between py-2.5 border-b border-ink/10 text-sm" data-testid={`tour-price-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}>
                      <span className="text-ink/80 flex items-center gap-2"><Users size={14} className="text-terra" /> {r.label}</span>
                      <span className="font-serif font-bold text-terra">₹{Number(r.price).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tour.inclusions?.length > 0 && (
              <div className="bg-white border border-ink/10 rounded-2xl p-6" data-testid="tour-detail-inclusions">
                <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-700 mb-3">Included</p>
                <ul className="space-y-2">{tour.inclusions.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-ink/80"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> {x}</li>)}</ul>
              </div>
            )}
            {tour.exclusions?.length > 0 && (
              <div className="bg-white border border-ink/10 rounded-2xl p-6" data-testid="tour-detail-exclusions">
                <p className="font-mono text-[11px] uppercase tracking-widest text-terra mb-3">Not included</p>
                <ul className="space-y-2">{tour.exclusions.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-ink/80"><X size={15} className="text-terra shrink-0 mt-0.5" /> {x}</li>)}</ul>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start bg-white border border-ink/10 shadow-lg shadow-ink/5 text-ink rounded-3xl p-7 relative overflow-hidden" data-testid="tour-detail-booking-card">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-terra/10 blur-3xl" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-terra">Per person from</p>
          <p className="font-serif text-5xl font-black mt-1 text-ink" data-testid="tour-detail-price">{tour.price}</p>
          <p className="text-sm text-ink/60 mt-1">{tour.duration}</p>
          <ul className="mt-5 space-y-1.5">{tour.features?.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-ink/80"><Check size={13} className="text-terra" /> {f}</li>)}</ul>
          {droneAddon && (
            <button type="button" onClick={() => setDrone((d) => !d)} aria-pressed={drone} data-testid="tour-drone-toggle"
              className={`mt-5 w-full text-left rounded-2xl border p-3.5 flex items-center gap-3 transition-colors ${drone ? "border-gold bg-gold/15" : "border-ink/15 hover:border-ink/40"}`}>
              <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${drone ? "bg-gold border-gold text-ink" : "border-ink/30"}`}>{drone && <Check size={13} />}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold flex items-center gap-1.5"><Video size={13} className="text-terra" /> Add {droneAddon.name}</span>
                <span className="block text-[11px] text-ink/60 mt-0.5">{droneAddon.videos} aerial clips · {droneAddon.minutes} min total · {droneAddon.tagline}</span>
              </span>
              <span className="font-serif font-bold text-terra shrink-0" data-testid="tour-drone-price">+₹{Number(droneAddon.price).toLocaleString("en-IN")}</span>
            </button>
          )}
          <a href={wa} target="_blank" rel="noreferrer" data-testid="tour-detail-whatsapp-button" className="mt-6 flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all"><MessageCircle size={16} /> Book on WhatsApp</a>
          {tour.pricing?.length > 0 && <PackageEnquiry tour={tour} drone={drone} droneAddon={droneAddon} />}
          <Link to="/#enquiry" data-testid="tour-detail-enquire-link" className="mt-2 flex items-center justify-center gap-2 w-full border border-ink/15 text-ink font-semibold py-3 rounded-full hover:bg-ink hover:text-white transition-colors"><Send size={15} /> Send an enquiry</Link>
          <p className="mt-4 text-[11px] text-ink/50 leading-relaxed">Free cancellation up to 24 hrs before pickup. Prices are per person for a group of 2; solo and large-group rates on request.</p>
        </aside>
      </section>
      <Footer />
      <WhatsAppDock />
      <WishlistDock />
    </main>
  );
}
